<template>
  <div class="lancar-compra q-mt-md">
    <q-separator class="q-mb-md" />
    <div class="text-subtitle2 q-mb-sm">Lançar na compra</div>

    <q-banner v-if="lancamento" rounded class="bg-green-1 text-green-10" role="status">
      {{ resumoLancamento }}
      <span v-if="temValor(lancamento.tarifa_centavos)">
        Tarifa R$ {{ reais(lancamento.tarifa_centavos) }} registrada, fora do custo.
      </span>
      <div v-if="conflito" class="q-mt-xs">
        Esta foto já tinha sido lançada com outros dados; vale o lançamento acima.
      </div>
      <div v-if="reaproveitarCompraDoCaixa" class="q-mt-xs">{{ avisoCompraAberta }}</div>
      <template #action>
        <q-btn flat color="green-10" label="Lançar outra foto" @click="$emit('proxima')" />
      </template>
    </q-banner>

    <template v-else>
      <div class="text-caption text-grey-8 q-mb-sm">{{ resumoLeitura }}</div>
      <div class="row q-col-gutter-sm">
        <div class="col-12 col-sm-6">
          <q-input
            v-if="compra"
            :model-value="nomeFornecedorCompra"
            label="Fornecedor"
            hint="Compra aberta"
            disable
          />
          <q-select
            v-else
            v-model="fornecedorId"
            :options="fornecedores"
            option-label="nome"
            option-value="id"
            emit-value
            map-options
            use-input
            input-debounce="500"
            clearable
            label="Fornecedor"
            :hint="fornecedorSugerido ? 'Último fornecedor de bolão' : ''"
            :error="Boolean(errosCampos.fornecedor_id)"
            :error-message="errosCampos.fornecedor_id"
            :disable="enviando"
            @filter="buscarFornecedor"
            @update:model-value="escolherFornecedor"
          />
        </div>
        <div class="col-12 col-sm-6">
          <q-select
            v-model="produtoId"
            :options="produtosFiltrados"
            option-label="nome"
            option-value="id"
            emit-value
            map-options
            use-input
            input-debounce="300"
            clearable
            label="Produto"
            :hint="produtoSugerido ? 'Sugerido pela modalidade' : ''"
            :error="Boolean(errosCampos.produto_id)"
            :error-message="errosCampos.produto_id"
            :disable="enviando"
            @filter="filtrarProdutos"
            @update:model-value="produtoSugerido = false"
          />
        </div>
        <div v-if="avisoEstoque" class="col-12 text-caption" :class="avisoEstoque.classe" role="status">
          <q-icon :name="avisoEstoque.icone" size="16px" class="q-mr-xs" />
          <span v-for="(linha, i) in avisoEstoque.linhas" :key="i">{{ i ? ' ' : '' }}{{ linha }}</span>
        </div>
        <div class="col-6 col-sm-3">
          <q-input
            v-model="quantidade"
            label="Cotas"
            mask="####"
            inputmode="numeric"
            :error="Boolean(errosCampos.quantidade)"
            :error-message="errosCampos.quantidade"
            :disable="enviando"
          />
        </div>
        <div class="col-6 col-sm-4">
          <q-input
            v-model="custo"
            label="Custo por cota"
            prefix="R$"
            inputmode="decimal"
            :hint="custoEditado ? '' : 'Valor da cota, sem tarifa'"
            :error="Boolean(errosCampos.custo_cota_centavos)"
            :error-message="errosCampos.custo_cota_centavos"
            :disable="enviando"
            @update:model-value="custoEditado = true"
          />
        </div>
        <div class="col-12 col-sm-5 flex items-center">
          <span v-if="totalCompra" class="text-body2">{{ totalCompra }}</span>
        </div>
      </div>
      <div v-if="temValor(documento.tarifa_centavos)" class="text-caption text-grey-8 q-mt-xs">
        Tarifa impressa R$ {{ reais(documento.tarifa_centavos) }}: fica registrada com a foto e não entra no custo.
      </div>
      <div class="row items-center justify-between q-mt-sm q-gutter-y-sm">
        <div>
          <q-checkbox
            v-model="conferido"
            label="Conferi a leitura com a foto"
            :disable="enviando"
            @update:model-value="limparErroConferencia"
          />
          <div v-if="errosCampos.conferido" class="text-negative text-caption">{{ errosCampos.conferido }}</div>
        </div>
        <q-btn
          color="positive"
          label="Lançar na compra"
          :loading="enviando"
          :disable="bloqueado || enviando"
          @click="lancar(false)"
        />
      </div>
      <div v-if="bloqueado" class="text-caption text-grey-8 q-mt-xs">
        Salve a revisão antes de lançar.
      </div>
      <div v-if="reaproveitarCompraDoCaixa" class="text-caption text-grey-8 q-mt-xs">{{ avisoCompraAberta }}</div>

      <q-banner v-if="cotasRepetidas.length" rounded class="bg-orange-1 text-orange-10 q-mt-md" role="alert">
        Esta cota parece já ter sido lançada:
        <span v-for="(repetida, i) in cotasRepetidas" :key="repetida.capture_id">{{ descreverRepetida(repetida, i) }}</span>.
        Confira se não é a mesma cota fotografada de novo.
        <template #action>
          <q-btn flat color="orange-10" label="Não lançar" @click="cotasRepetidas = []" />
          <q-btn flat color="orange-10" label="Lançar mesmo assim" :disable="enviando" @click="lancar(true)" />
        </template>
      </q-banner>

      <div v-if="erro" class="text-negative q-mt-sm" role="alert">
        {{ erro }}
        <q-btn
          v-if="pedidoPendente"
          flat
          dense
          color="primary"
          label="Tentar novamente"
          :disable="enviando"
          @click="reenviar"
        />
      </div>
    </template>
  </div>
</template>

<script>
import axios from '@/axios-auth';
import api from '@/services/boloes-foto';
import { centavosParaInput, inputParaCentavos } from '@/components/Caixas/boloes-foto/dinheiro';

const semAcento = texto => String(texto || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9+]/g, '');
const statusHttp = error => Number(error && (error.status || (error.response && error.response.status)));
const errosDaApi = error => {
  const body = error && error.response && error.response.data;
  return (body && body.errors && typeof body.errors === 'object') ? body.errors : {};
};

export default {
  name: 'lancar-compra-boloes-foto',
  emits: ['lancada', 'proxima'],
  props: {
    captura: { type: Object, required: true },
    compraAberta: { type: Object, default: null },
    reaproveitarCompraDoCaixa: { type: Boolean, default: false },
    bloqueado: { type: Boolean, default: false },
  },
  data() {
    return {
      fornecedorId: null,
      fornecedores: [],
      fornecedorSugerido: false,
      fornecedorEscolhido: false,
      conferido: false,
      produtoId: null,
      produtoSugerido: false,
      produtosFiltrados: [],
      quantidade: '1',
      custo: null,
      custoEditado: false,
      enviando: false,
      erro: null,
      errosCampos: {},
      cotasRepetidas: [],
      pedidoPendente: null,
      conflito: false,
      compraDoCaixa: null,
      situacaoEstoque: null,
      consultaEstoque: 0,
      avisoCompraAberta: 'A compra fica aberta até ser finalizada no caixa.',
    };
  },
  computed: {
    documento() {
      const extracao = this.captura.extraction || {};
      return extracao.document || {};
    },
    lancamento() {
      return this.captura.lancamento || null;
    },
    produtos() {
      const getters = this.$store && this.$store.getters || {};
      return getters.produtosEstoque || [];
    },
    compra() {
      return this.compraAberta || this.compraDoCaixa;
    },
    nomeFornecedorCompra() {
      const fornecedor = this.compra && this.compra.fornecedor;
      return fornecedor && fornecedor.nome ? fornecedor.nome : `Compra nº ${this.compra.id}`;
    },
    resumoLeitura() {
      const doc = this.documento;
      const partes = [
        doc.modalidade,
        doc.concurso ? `concurso ${doc.concurso}` : null,
        doc.identificacao_cota ? `cota ${doc.identificacao_cota}${doc.total_cotas ? ` de ${doc.total_cotas}` : ''}` : null,
        this.temValor(doc.valor_cota_centavos) ? `valor da cota R$ ${this.reais(doc.valor_cota_centavos)}` : null,
        this.temValor(doc.tarifa_centavos) ? `tarifa R$ ${this.reais(doc.tarifa_centavos)}` : null,
      ].filter(Boolean);
      return `Da foto: ${partes.join(', ') || 'nenhum campo lido'}. Confira e ajuste fornecedor, produto, cotas e custo.`;
    },
    resumoLancamento() {
      const { quantidade, custo_cota_centavos: custoCota } = this.lancamento;
      const custo = this.temValor(custoCota) ? ` × R$ ${this.reais(custoCota)}` : '';
      return `Lançada na compra nº ${this.lancamento.movcompra_id}: ${quantidade} ${quantidade === 1 ? 'cota' : 'cotas'}`
        + `${custo} = R$ ${this.reais(this.lancamento.valor_centavos)}.`;
    },
    avisoEstoque() {
      if (this.lancamento) return null;
      if (!this.produtoId) {
        const modalidade = this.documento.modalidade;
        return modalidade ? {
          classe: 'text-orange-10',
          icone: 'help_outline',
          linhas: [`Nenhum produto do cadastro parece com «${modalidade}». Escolha o produto.`],
        } : null;
      }
      const situacao = this.situacaoEstoque;
      if (!situacao || !situacao.produto || situacao.produto.id !== this.produtoId) return null;
      const { nome } = situacao.produto;
      const produto = situacao.concurso ? `${nome}, concurso ${situacao.concurso},` : nome;
      const troca = 'Se não for este produto, troque no campo Produto.';
      if (situacao.situacao === 'sem_concurso') {
        return {
          classe: 'text-orange-10',
          icone: 'warning',
          linhas: [`A foto não trouxe o concurso: informe-o na conferência para lançar ${nome}.`, troca],
        };
      }
      const linhas = situacao.situacao === 'soma'
        ? [`${produto} já existe no estoque (${situacao.estoque.quantidade} em estoque): a compra soma a ele.`]
        : [`${produto} ainda não está no estoque: entra como novo ao finalizar a compra.`];
      if (situacao.situacao !== 'soma' && situacao.quantidade_em_outros_concursos > 0) {
        linhas.push(`O produto já tem ${situacao.quantidade_em_outros_concursos} em outros concursos.`);
      }
      if (situacao.na_compra) {
        linhas.push(`Esta compra já tem ${situacao.na_compra.quantidade} deste produto e concurso; a cota soma a eles.`);
      }
      linhas.push(troca);
      return {
        classe: situacao.situacao === 'soma' ? 'text-green-10' : 'text-blue-10',
        icone: situacao.situacao === 'soma' ? 'inventory_2' : 'add_box',
        linhas,
      };
    },
    totalCompra() {
      const cotas = Number(this.quantidade);
      const custo = inputParaCentavos(this.custo);
      if (!Number.isInteger(cotas) || cotas < 1 || !(custo > 0)) return null;
      return `${cotas} × R$ ${this.reais(custo)} = R$ ${this.reais(cotas * custo)}`;
    },
  },
  watch: {
    produtos: {
      immediate: true,
      handler() {
        this.produtosFiltrados = this.produtos;
        this.sugerirProduto();
      },
    },
    documento() {
      this.atualizarCustoSugerido();
      this.sugerirProduto();
      this.consultarEstoque();
    },
    produtoId() {
      this.consultarEstoque();
    },
    compra() {
      this.consultarEstoque();
    },
    'captura.id': {
      immediate: true,
      handler() {
        this.conferido = false;
        this.fornecedorSugerido = false;
        this.produtoId = null;
        this.produtoSugerido = false;
        this.conflito = false;
        this.custoEditado = false;
        this.quantidade = '1';
        this.erro = null;
        this.errosCampos = {};
        this.cotasRepetidas = [];
        this.pedidoPendente = null;
        this.compraDoCaixa = null;
        this.situacaoEstoque = null;
        this.atualizarCustoSugerido();
        this.sugerirProduto();
        this.sugerirFornecedor();
        this.consultarEstoque();
      },
    },
  },
  methods: {
    reais(centavos) {
      return centavosParaInput(centavos) || '';
    },
    temValor(centavos) {
      return Number.isInteger(centavos);
    },
    escolherFornecedor() {
      this.fornecedorEscolhido = true;
      this.fornecedorSugerido = false;
    },
    limparErroConferencia() {
      delete this.errosCampos.conferido;
    },
    async sugerirFornecedor() {
      if (this.compraAberta || this.lancamento) return;
      const captureId = this.captura.id;
      const { fornecedor, compra_aberta: compraDoCaixa } = await api.fornecedorSugerido(captureId).catch(() => ({}));
      if (this.captura.id !== captureId) return;
      if (this.reaproveitarCompraDoCaixa && compraDoCaixa) this.compraDoCaixa = compraDoCaixa;
      if (!fornecedor || this.fornecedorId || this.fornecedorEscolhido) return;
      this.fornecedores = [fornecedor];
      this.fornecedorId = fornecedor.id;
      this.fornecedorSugerido = true;
    },
    dataHora(iso) {
      const data = new Date(iso);
      return Number.isNaN(data.getTime()) ? iso : data.toLocaleString('pt-BR');
    },
    descreverRepetida(repetida, indice) {
      return [
        indice ? '; ' : '',
        `foto nº ${repetida.capture_id}`,
        repetida.movcompra_id ? ` na compra nº ${repetida.movcompra_id}` : '',
        repetida.lancado_em ? ` em ${this.dataHora(repetida.lancado_em)}` : '',
      ].join('');
    },
    async consultarEstoque() {
      this.consultaEstoque += 1;
      const consulta = this.consultaEstoque;
      if (!this.produtoId || this.lancamento) {
        this.situacaoEstoque = null;
        return;
      }
      const params = { produto_id: this.produtoId };
      if (this.compra) params.movcompra_id = this.compra.id;
      const situacao = await api.situacaoEstoque(this.captura.id, params).catch(() => null);
      if (consulta === this.consultaEstoque) this.situacaoEstoque = situacao;
    },
    sugerirProduto() {
      if (this.produtoId || !this.documento.modalidade) return;
      const modalidade = semAcento(this.documento.modalidade);
      const produto = this.produtos.find(item => semAcento(item.nome).includes(modalidade));
      if (produto) {
        this.produtoId = produto.id;
        this.produtoSugerido = true;
      }
    },
    atualizarCustoSugerido() {
      if (this.custoEditado) return;
      const valorCota = this.documento.valor_cota_centavos;
      this.custo = this.temValor(valorCota) ? this.reais(valorCota) : null;
    },
    filtrarProdutos(texto, update) {
      update(() => {
        const busca = semAcento(texto);
        this.produtosFiltrados = this.produtos.filter(produto => semAcento(produto.nome).includes(busca));
      });
    },
    buscarFornecedor(texto, update) {
      axios.get('/cadastros', {
        params: { ativo: 1, funcionario: 1, fornecedor: 1, busca: texto },
      }).then(res => {
        update(() => {
          this.fornecedores = res && res.data && res.data.data ? res.data.data.cadastros || [] : [];
        });
      }).catch(() => {
        update(() => {
          this.fornecedores = [];
        });
      });
    },
    montarPedido(confirmarDuplicidade) {
      const erros = {};
      const custoCota = inputParaCentavos(this.custo);
      const quantidade = Number(this.quantidade);
      if (!this.produtoId) erros.produto_id = 'Escolha o produto.';
      if (!Number.isInteger(quantidade) || quantidade < 1) erros.quantidade = 'Informe as cotas.';
      if (!(custoCota > 0)) erros.custo_cota_centavos = 'Informe o custo por cota em reais.';
      if (!this.compra && !this.fornecedorId) erros.fornecedor_id = 'Escolha o fornecedor.';
      if (!this.conferido) erros.conferido = 'Confira a leitura com a foto antes de lançar.';
      this.errosCampos = erros;
      if (Object.keys(erros).length) return null;

      const pedido = {
        base_revision: Number(this.captura.extraction.current_revision),
        produto_id: this.produtoId,
        quantidade,
        custo_cota_centavos: custoCota,
        conferido: true,
      };
      if (this.compra) pedido.movcompra_id = this.compra.id;
      else pedido.fornecedor_id = this.fornecedorId;
      if (confirmarDuplicidade) pedido.confirmar_duplicidade = true;
      return pedido;
    },
    lancar(confirmarDuplicidade) {
      if (this.enviando || this.bloqueado) return;
      const pedido = this.montarPedido(confirmarDuplicidade);
      if (!pedido) return;
      this.enviar(pedido);
    },
    reenviar() {
      if (this.pedidoPendente) this.enviar(this.pedidoPendente);
    },
    async enviar(pedido) {
      const captureId = this.captura.id;
      this.enviando = true;
      this.erro = null;
      this.pedidoPendente = pedido;
      try {
        const resposta = await api.lancar(captureId, pedido);
        if (this.captura.id !== captureId) return;
        this.pedidoPendente = null;
        this.cotasRepetidas = [];
        this.$emit('lancada', resposta);
      } catch (error) {
        if (this.captura.id !== captureId) return;
        this.tratarErro(error);
      } finally {
        this.enviando = false;
      }
    },
    tratarErro(error) {
      const status = statusHttp(error);
      const erros = errosDaApi(error);
      if (status === 409 && Array.isArray(erros.cota_ja_lancada)) {
        this.pedidoPendente = null;
        this.cotasRepetidas = erros.cota_ja_lancada;
        return;
      }
      if (status === 409 && erros.lancamento) {
        this.pedidoPendente = null;
        this.conflito = true;
        this.$emit('lancada', { replay: true, lancamento: erros.lancamento });
        return;
      }
      if (status && status < 500) {
        this.pedidoPendente = null;
        this.errosCampos = Object.keys(erros).reduce((campos, campo) => {
          if (typeof erros[campo] === 'string') campos[campo] = erros[campo];
          return campos;
        }, {});
      }
      this.erro = (error && error.message) || 'Não foi possível lançar na compra.';
    },
  },
};
</script>
